import { Capacitor } from "@capacitor/core";
import { useTranslate } from "@tolgee/react";
import React, { Suspense } from "react";
import { getAddressImageSrc } from "../features/addresses/helpers.js";

// `@capacitor/google-maps` calls the native plugin at module load
// (`addListener('isMapInFocus')`), so it must only be imported on the native
// path: loading it lazily keeps web, Android and shells without the native
// plugin free of that side effect.
const NativeIosMapPreview = React.lazy(() =>
  import("./NativeIosMapPreview.js").then(m => ({
    default: m.NativeIosMapPreview,
  }))
);

export interface MapPreviewProps {
  width: number;
  height: number;
  zoom: number;
  location: { lat: number; lng: number };
  href?: string;
  isGreyedOut?: boolean;
  withMarker?: boolean;
}

function StaticMapPreview({
  width,
  height,
  zoom,
  location,
  href,
  isGreyedOut = false,
  withMarker = false,
}: MapPreviewProps) {
  const { t } = useTranslate("Global");
  const url = getAddressImageSrc(
    { location: { __typename: "Location", ...location } },
    width * 2,
    height * 2,
    zoom,
    isGreyedOut,
    withMarker
  );
  const style: React.CSSProperties = {
    width: `${width}px`,
    height: `${height}px`,
    flexShrink: 0,
    flexGrow: 0,
    backgroundImage: `url(${url})`,
    backgroundRepeat: "no-repeat",
    backgroundPosition: "center",
    backgroundSize: "cover",
  };
  if (href) {
    return (
      <a
        target="_blank"
        rel="noreferrer nofollow"
        href={href}
        style={style}
        aria-label={t("Open map", { ns: "Global" })}
      />
    );
  }
  return <div style={style} />;
}

export function MapPreview(props: MapPreviewProps) {
  // A greyed-out preview is the address-less placeholder: the native map has
  // no greyed style, so it uses the static image on every platform.
  if (
    !props.isGreyedOut &&
    Capacitor.isNativePlatform() &&
    Capacitor.getPlatform() === "ios" &&
    // CAP6-COMPAT(MEIS-13783): old Capacitor 6 shells lack the native CapacitorGoogleMaps plugin, so they get the static image. Remove once the Cap 6 store builds (iOS <= 84 / Android <= 75) are gone.
    Capacitor.isPluginAvailable("CapacitorGoogleMaps")
  ) {
    return (
      <Suspense
        fallback={
          <div
            style={{
              width: `${props.width}px`,
              height: `${props.height}px`,
              flexShrink: 0,
              flexGrow: 0,
            }}
          />
        }
      >
        <NativeIosMapPreview {...props} />
      </Suspense>
    );
  }
  return <StaticMapPreview {...props} />;
}
